import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ProgressBar,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '../../constants/appConfig';
import { api } from '../../api';
import { trackAdminUiEvent, ADMIN_UI_EVENTS } from '../../utils/adminAnalytics';

const STORAGE_KEY = 'nudgesmart_setup_guide_v1';

/**
 * Is this string safe to interpolate as the HOST of the theme-editor deep link?
 *
 * APP_CONFIG.getThemeExtensionDeepLink (constants/appConfig.js:44) builds
 * `https://${myshopifyDomain}/admin/themes/...` with no validation whatsoever.
 * An empty domain — which is the DEFAULT for this prop, and the real state while
 * the shop query is still in flight — yields `https:///admin/themes/...`, which
 * every browser normalises to the CURRENT origin. So `window.open` succeeds, the
 * merchant lands on a copy of the admin app instead of their theme editor, and
 * nothing anywhere reports a failure.
 *
 * Hostname shape is the right check, and it is also the security check: it
 * rejects `evil.com/x`, `//evil.com` and anything with whitespace or a scheme,
 * so nothing can smuggle a path or a different origin into the URL.
 */
const isUsableShopDomain = (domain) =>
  typeof domain === 'string' && /^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(domain);

/**
 * SetupGuide Component
 *
 * A 5-step onboarding checklist following Shopify's official homepage pattern.
 * Implements the Zeigarnik Effect by starting progress at 20% (Step 1 pre-completed).
 *
 * Steps:
 * 1. Account Created (auto-completed)
 * 2. Enable Theme Extension (required for campaigns to display)
 * 3. Choose Template
 * 4. Customize & Publish
 * 5. Connect Email (optional)
 */
const SetupGuide = ({
  // Have ALL the derived inputs below resolved? Defaults FALSE so a caller that forgets to
  // pass it emits nothing rather than emitting garbage — see the effect below for why.
  inputsSettled = false,
  hasThemeExtension = false,
  hasAnyCampaigns = false,
  hasActiveCampaign = false,
  hasEmailIntegration = false,
  myshopifyDomain = '',
  onRefreshExtensionStatus = () => {},
  onTemplateSelect,
  compact = false,
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const [dismissedAt, setDismissedAt] = useState(null);
  const [clickedEnable, setClickedEnable] = useState(false);
  // Non-null whenever the theme-editor deep link could NOT be opened. Rendered
  // as a banner OUTSIDE the collapsible steps region — the collapsed state sets
  // maxHeight:0 + inert on that div, so a message placed inside it would be
  // invisible exactly when the merchant clicked the collapsed "Continue" CTA.
  const [deepLinkError, setDeepLinkError] = useState(null);

  // Load persisted state
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setDismissedAt(parsed.dismissedAt);
        if (parsed.isExpanded !== undefined) {
          setIsExpanded(parsed.isExpanded);
        }
      }
    } catch (e) {
      console.error('Error loading setup guide state:', e);
    }
  }, []);

  // Save state to localStorage
  const saveState = useCallback((updates) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const current = stored ? JSON.parse(stored) : {};
      const newState = { ...current, ...updates };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    } catch (e) {
      console.error('Error saving setup guide state:', e);
    }
  }, []);

  // Calculate completed steps based on actual data
  const getCompletedSteps = useCallback(() => {
    const steps = ['account_created']; // Always completed (Zeigarnik Effect)
    if (hasThemeExtension) steps.push('theme_extension_enabled');
    if (hasAnyCampaigns) steps.push('template_chosen');
    if (hasActiveCampaign) steps.push('campaign_published');
    if (hasEmailIntegration) steps.push('email_connected');
    return steps;
  }, [hasThemeExtension, hasAnyCampaigns, hasActiveCampaign, hasEmailIntegration]);

  const completedSteps = getCompletedSteps();
  const totalSteps = 5;
  const completedCount = completedSteps.length;
  const progressPercent = Math.round((completedCount / totalSteps) * 100);

  // Emit an onboarding-step event each time a NEW step is completed in-session.
  // Derived state has no click event, so we diff against previously-seen steps.
  // First run seeds the set WITHOUT emitting, so a returning merchant's already-
  // completed steps don't fire as fresh completions.
  //
  // ⚠️ THE SEED IS ONLY VALID ONCE EVERY INPUT HAS SETTLED, and they do not settle together.
  // hasAnyCampaigns/hasActiveCampaign are resolved before this component mounts (the parent
  // renders a skeleton until then), but hasThemeExtension and hasEmailIntegration arrive from
  // DEFERRED fetches — i.e. AFTER the first effect run. Seeding from that half-resolved
  // snapshot meant both later transitioned false -> true and fired as "completions", so a
  // fully-onboarded merchant emitted ~2 phantom ns_onboarding_step_completed per mount.
  //
  // This effect also sits ABOVE the visibility early-returns further down, so it runs even
  // when the component renders null (guide finished or dismissed) — the gate is what stops
  // those merchants paying for events too.
  const emittedStepsRef = useRef(null);
  useEffect(() => {
    if (!inputsSettled) return;   // never seed from a half-resolved snapshot
    if (emittedStepsRef.current === null) {
      emittedStepsRef.current = new Set(completedSteps);
      return;
    }
    completedSteps.forEach((step) => {
      if (step === 'account_created') return; // auto-completed, not a real action
      if (!emittedStepsRef.current.has(step)) {
        emittedStepsRef.current.add(step);
        trackAdminUiEvent(api, ADMIN_UI_EVENTS.ONBOARDING_STEP_COMPLETED, {
          step,
          progress_percent: progressPercent,
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsSettled, completedSteps.join(',')]);

  // Define all steps
  const steps = [
    {
      id: 'account_created',
      title: 'Account Created',
      description: 'Your Nudgesmart account is ready',
      completed: true, // Always completed
      action: null,
    },
    {
      id: 'theme_extension_enabled',
      title: 'Enable Theme Extension',
      description: completedSteps.includes('theme_extension_enabled')
        ? 'Nudgesmart is enabled in your theme'
        : 'Required: Enable Nudgesmart in your theme so campaigns can display on your store',
      completed: completedSteps.includes('theme_extension_enabled'),
      action: completedSteps.includes('theme_extension_enabled')
        ? null
        : {
            content: clickedEnable ? 'Check Status' : 'Enable in Theme Editor',
            onAction: () => {
              if (clickedEnable) {
                // Second click: refresh status
                onRefreshExtensionStatus();
              } else {
                // First click: open the deep link, and ONLY THEN show the
                // refresh option.
                //
                // `clickedEnable` used to be set unconditionally, which latched
                // the whole step into a lie: the button relabelled to "Check
                // Status" and the helper text told the merchant to "click Check
                // Status after saving in the theme editor" — an editor they were
                // never taken to, because an empty `myshopifyDomain` (the prop's
                // own default, and the real value while the shop query is in
                // flight) produces a URL that resolves to this very app.
                //
                // So: validate the host first, and latch only on a window we can
                // prove opened. Anything else surfaces a banner instead of
                // silently advancing the step.
                if (!isUsableShopDomain(myshopifyDomain)) {
                  setDeepLinkError(
                    "We're still loading your store details. Give it a moment and try again."
                  );
                  return;
                }
                const deepLink = APP_CONFIG.getThemeExtensionDeepLink(myshopifyDomain);
                const opened = window.open(deepLink, '_blank');
                if (!opened) {
                  // Popup blocker, or an embedded context that refused the open.
                  setDeepLinkError(
                    'Your browser blocked the theme editor window. Allow pop-ups for this page, then try again.'
                  );
                  return;
                }
                setDeepLinkError(null);
                setClickedEnable(true);
              }
            },
          },
      helperText: !completedSteps.includes('theme_extension_enabled')
        ? (clickedEnable
            ? '👆 Click "Check Status" after saving in the theme editor'
            : '💡 This opens your theme editor. Just click "Save" to enable.')
        : null,
    },
    {
      id: 'template_chosen',
      title: 'Choose a Template',
      description: 'Pick a campaign template to get started',
      completed: completedSteps.includes('template_chosen'),
      action: {
        content: 'Browse Templates',
        onAction: () => {
          if (onTemplateSelect) {
            onTemplateSelect();
          } else {
            // Scroll to template section or navigate
            const templateSection = document.getElementById('template-selection');
            if (templateSection) {
              templateSection.scrollIntoView({ behavior: 'smooth' });
            }
          }
        },
      },
    },
    {
      id: 'campaign_published',
      title: 'Customize & Publish',
      description: 'Customize your campaign and set it live',
      completed: completedSteps.includes('campaign_published'),
      action: {
        content: 'View Campaigns',
        onAction: () => navigate('/campaigns'),
      },
    },
    {
      id: 'email_connected',
      title: 'Connect Email (Optional)',
      description: 'Sync captured emails with your email provider',
      completed: completedSteps.includes('email_connected'),
      action: {
        content: 'Connect Provider',
        onAction: () => navigate('/integrations'),
      },
      optional: true,
    },
  ];

  // Find current step (first incomplete)
  const currentStepIndex = steps.findIndex(step => !step.completed);
  const currentStep = currentStepIndex >= 0 ? steps[currentStepIndex] : null;

  // Handle dismiss
  const handleDismiss = useCallback(() => {
    const now = new Date().toISOString();
    setDismissedAt(now);
    saveState({ dismissedAt: now });
  }, [saveState]);

  // Handle expand/collapse
  const handleToggleExpand = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    saveState({ isExpanded: newExpanded });
  }, [isExpanded, saveState]);

  // Rendered by BOTH variants — the compact one exposes the same step action, so
  // it can reach the same failure and must be able to report it.
  const deepLinkErrorBanner = deepLinkError ? (
    <s-banner tone="warning" heading="Couldn't open the theme editor">
      <s-text>{deepLinkError}</s-text>
    </s-banner>
  ) : null;

  // Don't show if all steps completed
  if (completedCount === totalSteps) {
    return null;
  }

  // Don't show if dismissed AND at least 4 steps are complete (only optional step remaining)
  // This prevents premature hiding while respecting user's dismiss preference for near-completion
  if (dismissedAt && completedCount >= 4) {
    return null;
  }

  // Compact variant for dashboard (showing remaining steps only)
  // Threshold is 4 to maintain ~75% completion with 5 total steps
  if (compact && completedCount >= 4) {
    const remainingSteps = steps.filter(s => !s.completed);
    if (remainingSteps.length === 0) return null;

    return (
      <s-section>
        <s-stack direction="block" gap="small">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-heading>
              Complete Setup ({totalSteps - completedCount} step remaining)
            </s-heading>
            {hasThemeExtension && (
              <s-button onClick={handleDismiss} variant="tertiary">
                Dismiss
              </s-button>
            )}
          </s-stack>
          {deepLinkErrorBanner}
          {remainingSteps.map((step) => (
            <s-stack key={step.id} direction="inline" gap="small" alignItems="center">
              <s-icon type="minus-circle" color="subdued" />
              <s-text>
                {step.title}
              </s-text>
              {step.action && (
                <s-button onClick={step.action.onAction}>
                  {step.action.content}
                </s-button>
              )}
            </s-stack>
          ))}
        </s-stack>
      </s-section>
    );
  }

  return (
    <s-section>
      <s-stack direction="block" gap="base">
        {/* Header */}
        <s-stack direction="inline" justifyContent="space-between" alignItems="center">
          <s-stack direction="inline" gap="small-200" alignItems="center">
            <s-heading>
              Get Started
            </s-heading>
            <s-text color="subdued">
              {completedCount} of {totalSteps} complete
            </s-text>
          </s-stack>
          <s-stack direction="inline" gap="small-200">
            <s-button
              onClick={handleToggleExpand}
              icon={isExpanded ? 'chevron-up' : 'chevron-down'}
              variant="tertiary"
              accessibilityLabel={isExpanded ? 'Collapse setup guide' : 'Expand setup guide'}
            />
            {hasThemeExtension && (
              <s-button onClick={handleDismiss} variant="tertiary">
                Dismiss
              </s-button>
            )}
          </s-stack>
        </s-stack>

        {/* Progress Bar */}
        <ProgressBar
          progress={progressPercent}
          tone="primary"
          size="small"
        />

        {deepLinkErrorBanner}

        {/* Steps List */}
        <div
          id="setup-guide-steps"
          style={{
            overflow: 'hidden',
            maxHeight: isExpanded ? '2000px' : '0',
            transition: 'max-height 0.3s ease-in-out',
          }}
          aria-hidden={!isExpanded}
          {...(!isExpanded ? { inert: 'true' } : {})}
        >
          <s-stack direction="block" gap="small">
            {steps.map((step, index) => {
              const isCurrentStep = index === currentStepIndex;

              return (
                <s-box
                  key={step.id}
                  padding="small"
                  background={isCurrentStep ? 'subdued' : undefined}
                  borderRadius="base"
                >
                  <s-stack direction="inline" gap="small" alignItems="start">
                    <div style={{ marginTop: '2px' }}>
                      {step.completed ? (
                        <s-icon type="check-circle" tone="success" />
                      ) : isCurrentStep ? (
                        <s-icon type="minus-circle" tone="info" />
                      ) : (
                        <s-icon type="minus-circle" color="subdued" />
                      )}
                    </div>
                    <s-stack direction="block" gap="small-300" alignItems="start">
                      <s-stack direction="inline" gap="small-200" alignItems="center">
                        {isCurrentStep ? (
                          <s-text type="strong">{step.title}</s-text>
                        ) : (
                          <s-text>{step.title}</s-text>
                        )}
                        {step.optional && (
                          <s-text color="subdued">
                            (Optional)
                          </s-text>
                        )}
                        {step.completed && (
                          <s-text tone="success">
                            Done
                          </s-text>
                        )}
                      </s-stack>
                      <s-paragraph color="subdued">
                        {step.description}
                      </s-paragraph>
                      {isCurrentStep && step.action && (
                        <div style={{ marginTop: '8px' }}>
                          <s-button
                            variant="primary"
                            onClick={step.action.onAction}
                          >
                            {step.action.content}
                          </s-button>
                          {step.helperText && (
                            <s-paragraph color="subdued">
                              {step.helperText}
                            </s-paragraph>
                          )}
                        </div>
                      )}
                    </s-stack>
                  </s-stack>
                </s-box>
              );
            })}
          </s-stack>
        </div>

        {/* Continue CTA when collapsed */}
        {!isExpanded && currentStep && (
          <s-button
            variant="primary"
            onClick={currentStep.action?.onAction}
          >
            Continue: {currentStep.title}
          </s-button>
        )}
      </s-stack>
    </s-section>
  );
};

export default SetupGuide;
